import { z } from "zod";
import { calendarDate } from "./campaigns";
import { expectedVersion, id, pageInput } from "./common";
import { NO_CONTROL_CHARS, longText, safeText } from "./text";
import { VISIBILITIES } from "./visibility";

/** The workboard's columns, in board order. */
export const TASK_STATUSES = ["todo", "in_progress", "blocked", "done", "cancelled"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

/** Which moves are allowed (the server enforces this; the UI only offers these). */
export const TASK_STATUS_TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  todo: ["in_progress", "blocked", "cancelled"],
  in_progress: ["todo", "blocked", "done", "cancelled"],
  blocked: ["todo", "in_progress", "cancelled"],
  done: ["in_progress"],
  cancelled: ["todo"],
};

/**
 * Evidence links: https only (no javascript:, data: or http: URLs), bounded length.
 * TSK-03: no invisible or control characters and no "user@" part, so the link shown
 * is the address it opens.
 */
const evidenceLink = z
  .string()
  .regex(NO_CONTROL_CHARS, "Contains characters that aren't allowed")
  .pipe(
    z
      .url({
        protocol: /^https$/,
        hostname: z.regexes.domain,
        error: "Links must start with https:// and be a full address",
      })
      .max(2048),
  )
  // TSK-05: exactly "https://", then a host with no "@" or backslash, as browsers read it.
  .refine(
    (url) => /^https:\/\/[^/\\?#@]+(?:[/?#]|$)/i.test(url),
    "Links can't contain a user name or password",
  );

const unique = (items: readonly string[]) => new Set(items).size === items.length;

const taskFields = z.object({
  title: safeText(2, 200),
  details: longText(4000),
  dueOn: calendarDate.nullable(),
  assigneeUserId: id.nullable(),
  visibility: z.enum(VISIBILITIES),
  evidenceLinks: z.array(evidenceLink).max(10),
  dependsOnTaskIds: z.array(id).max(20).refine(unique, "Each task only once"),
});

export const createTaskInput = taskFields.extend({ campaignId: id });
export type CreateTaskInput = z.infer<typeof createTaskInput>;

/** Only the fields being changed. */
export const updateTaskInput = taskFields.partial().extend({ taskId: id, expectedVersion });
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

export const setTaskStatusInput = z.object({
  taskId: id,
  status: z.enum(TASK_STATUSES),
  expectedVersion,
});

export const taskIdInput = z.object({ taskId: id });

export const listTasksInput = pageInput.extend({ campaignId: id });
export type ListTasksInput = z.infer<typeof listTasksInput>;
